/* Soria Natural — Klaviyo Onsite Reviews label translator (Slovenian).
 *
 * Klaviyo Reviews widget does NOT expose a Slovenian localization option in its
 * dashboard (only a handful of preset languages). This script runs in the
 * browser after the widget mounts and replaces visible English strings with
 * their Slovenian equivalents.
 *
 * It also handles Slovenian plurals for "mnenje" (review):
 *   1   -> 1 mnenje    (one)
 *   2   -> 2 mnenji    (two — dual)
 *   3,4 -> 3 mnenja    (few)
 *   5+  -> 5 mnenj     (other)
 *
 * The script uses MutationObserver to re-translate when Klaviyo lazily renders
 * additional review pages, sort changes, etc.
 *
 * If Klaviyo ever ships an official Slovenian, delete this file + its include.
 */
(function () {
  'use strict';

  // --- Slovenian plural rule for "review" / "mnenje" ---
  // CLDR rules: one = n%100==1, two = n%100==2, few = n%100 in {3,4}, other.
  function plural_mnenje(n) {
    var rem = Math.abs(n) % 100;
    if (rem === 1) return 'mnenje';
    if (rem === 2) return 'mnenji';
    if (rem === 3 || rem === 4) return 'mnenja';
    return 'mnenj';
  }

  // --- Slovenian plural for "month" / "mesec" / "pred ..." ---
  function plural_mesec(n) {
    var rem = Math.abs(n) % 100;
    if (rem === 1) return 'mesecem';     // pred 1 mesecem
    if (rem === 2) return 'mesecema';    // pred 2 mesecema
    if (rem === 3 || rem === 4) return 'meseci'; // pred 3 meseci
    return 'meseci';                     // pred 8 meseci
  }
  function plural_dan(n) {
    var rem = Math.abs(n) % 100;
    if (rem === 1) return 'dnem';
    if (rem === 2) return 'dnevoma';
    if (rem === 3 || rem === 4) return 'dnevi';
    return 'dnevi';
  }
  function plural_teden(n) {
    var rem = Math.abs(n) % 100;
    if (rem === 1) return 'tednom';
    if (rem === 2) return 'tednoma';
    if (rem === 3 || rem === 4) return 'tedni';
    return 'tedni';
  }
  function plural_leto(n) {
    var rem = Math.abs(n) % 100;
    if (rem === 1) return 'letom';
    if (rem === 2) return 'letoma';
    if (rem === 3 || rem === 4) return 'leti';
    return 'leti';
  }

  // --- Static string replacements (no number context) ---
  // Map English label -> Slovenian. Match exact text (after .trim()).
  var STATIC_MAP = {
    'Customer reviews': 'Mnenja kupcev',
    'Reviews': 'Mnenja',
    'Write a review': 'Napišite mnenje',
    'Write a Review': 'Napišite mnenje',
    'Verified buyer': 'Preverjen kupec',
    'Verified Buyer': 'Preverjen kupec',
    'Most relevant': 'Najbolj relevantna',
    'Most Relevant': 'Najbolj relevantna',
    'Most recent': 'Najnovejša',
    'Most Recent': 'Najnovejša',
    'Highest rated': 'Najvišja ocena',
    'Highest Rated': 'Najvišja ocena',
    'Lowest rated': 'Najnižja ocena',
    'Lowest Rated': 'Najnižja ocena',
    'All ratings': 'Vse ocene',
    'All Ratings': 'Vse ocene',
    'With media': 'S sliko',
    'With Media': 'S sliko',
    'With photos': 'S fotografijami',
    'Search reviews': 'Iskanje mnenj',
    'Search Reviews': 'Iskanje mnenj',
    'Helpful': 'Koristno',
    'Report': 'Prijavi',
    'Reply': 'Odgovori',
    'Load more': 'Naloži več',
    'Load More': 'Naloži več',
    'Show more': 'Pokaži več',
    'Show less': 'Pokaži manj',
    'Anonymous': 'Anonimno',
    'Sort by': 'Razvrsti po',
    'Filter': 'Filtriraj',
    'Submit': 'Pošlji',
    'Cancel': 'Prekliči',
    'Send': 'Pošlji',
    'Save': 'Shrani',
    'Edit': 'Uredi',
    'Delete': 'Izbriši',
    'Recommended': 'Priporočeno',
    'Not recommended': 'Ni priporočeno'
  };

  // --- Patterns that need number-aware translation ---
  // Each entry: { re, build } — `re` extracts the count; `build(n)` returns SL.
  var DYNAMIC = [
    // "5 reviews" / "1 review"
    { re: /^\s*(\d+)\s+reviews?\s*$/i, build: function (n) { return n + ' ' + plural_mnenje(n); } },
    // "X month(s) ago"
    { re: /^\s*(\d+)\s+months?\s+ago\s*$/i, build: function (n) { return 'pred ' + n + ' ' + plural_mesec(n); } },
    { re: /^\s*1\s+month\s+ago\s*$/i, build: function () { return 'pred 1 mesecem'; } },
    // "X day(s) ago"
    { re: /^\s*(\d+)\s+days?\s+ago\s*$/i, build: function (n) { return 'pred ' + n + ' ' + plural_dan(n); } },
    // "X week(s) ago"
    { re: /^\s*(\d+)\s+weeks?\s+ago\s*$/i, build: function (n) { return 'pred ' + n + ' ' + plural_teden(n); } },
    // "X year(s) ago"
    { re: /^\s*(\d+)\s+years?\s+ago\s*$/i, build: function (n) { return 'pred ' + n + ' ' + plural_leto(n); } },
    // "X hour(s) ago"
    { re: /^\s*(\d+)\s+hours?\s+ago\s*$/i, build: function (n) { return n === 1 ? 'pred 1 uro' : (n === 2 ? 'pred 2 urama' : 'pred ' + n + ' urami'); } },
    // "yesterday" / "today"
    { re: /^\s*yesterday\s*$/i, build: function () { return 'včeraj'; } },
    { re: /^\s*today\s*$/i, build: function () { return 'danes'; } },
    // "Reviews 5" (tab label with count after) — keep label translated by static rule above; this catches the count node if separate.
  ];

  // Containers that we know belong to Klaviyo Reviews. We translate only inside
  // these to avoid touching unrelated text on the page.
  var CONTAINER_SELECTORS = [
    '[class*="klaviyo"]',
    '[class*="Klaviyo"]',
    '[class*="kl-reviews"]',
    '[class*="klr-"]',
    '[id*="klaviyo"]',
    '[data-klaviyo]',
    // Fallback wrapper class — adjust if Klaviyo uses something else.
    '.klaviyo-reviews-widget',
    '.klaviyo-onsite-reviews-widget'
  ];

  function translateTextNode(node) {
    var text = node.nodeValue;
    if (!text) return;
    var trimmed = text.trim();
    if (!trimmed) return;

    // Try dynamic patterns first (more specific).
    for (var i = 0; i < DYNAMIC.length; i++) {
      var m = trimmed.match(DYNAMIC[i].re);
      if (m) {
        var n = m[1] ? parseInt(m[1], 10) : 1;
        var replacement = DYNAMIC[i].build(n);
        if (replacement && replacement !== trimmed) {
          node.nodeValue = text.replace(trimmed, replacement);
        }
        return;
      }
    }

    // Static map.
    if (STATIC_MAP.hasOwnProperty(trimmed)) {
      var sl = STATIC_MAP[trimmed];
      if (sl !== trimmed) {
        node.nodeValue = text.replace(trimmed, sl);
      }
    }
  }

  function walkAndTranslate(root) {
    if (!root || !root.querySelectorAll) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var n;
    while ((n = walker.nextNode())) {
      translateTextNode(n);
    }
  }

  function runOnce() {
    CONTAINER_SELECTORS.forEach(function (sel) {
      var nodes = document.querySelectorAll(sel);
      for (var i = 0; i < nodes.length; i++) walkAndTranslate(nodes[i]);
    });
  }

  // Initial pass + observe future DOM changes.
  function start() {
    runOnce();
    // Debounced observer — Klaviyo can paint many small updates as user scrolls.
    var pending = null;
    var obs = new MutationObserver(function () {
      if (pending) return;
      pending = window.requestAnimationFrame(function () {
        pending = null;
        runOnce();
      });
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
